import type { EncounterProgram } from "@curai/js-fhir-tools";
import { z } from "zod";

const icd10CodingSchema = z.object({
  code: z.string(),
  system: z.string(),
  display: z.string(),
  version: z.string(),
});

const dxBillingCodingSchema = z.object({
  code: z.string().min(1, "ICD-10 code is required"),
  system: z.string().min(1, "ICD-10 code system is required"),
  display: z.string().min(1, "ICD-10 code display is required"),
  version: z.string().min(1, "ICD-10 code version is required"),
});

const childEncounterSchema = z.object({
  episodeOfCareId: z.string().min(1, "Episode of Care ID is required"),
  episodeOfCareTitle: z.string().min(1, "Episode of Care Title is required"),
  encounterId: z.string().min(1, "Encounter ID is required"),
  conditionCodeValue: z.string(),
  icd10Codes: z
    .array(
      icd10CodingSchema.refine((data) => data.code.length > 0, {
        message: "Empty codes are not allowed",
      }),
    )
    .min(1, "At least one ICD-10 code is required")
    .max(12, "At most 12 ICD-10 codes are allowed")
    .refine(
      (codes) => new Set(codes.map(({ code }) => code)).size === codes.length,
      { message: "Duplicate ICD-10 codes are not allowed" },
    ),
});

const strictLegacyIcd10Schema = z.object({
  childEncounters: z
    .array(childEncounterSchema)
    .min(1, "At least one episode of care is required"),
  primaryIcd10Code: icd10CodingSchema.refine((data) => data.code.length > 0, {
    message: "Exactly one primary ICD-10 code is required",
  }),
});

const relaxedLegacyIcd10Schema = z.object({
  childEncounters: z.array(
    childEncounterSchema.extend({
      icd10Codes: z
        .array(icd10CodingSchema)
        .max(12, "At most 12 ICD-10 codes are allowed")
        .refine(
          (codes) => {
            const nonblankCodes = codes.map(({ code }) => code).filter(Boolean);
            return new Set(nonblankCodes).size === nonblankCodes.length;
          },
          { message: "Duplicate ICD-10 codes are not allowed" },
        ),
    }),
  ),
  primaryIcd10Code: icd10CodingSchema,
});

const visitClosureFormSchemaWithDefaults = z.object({
  rejectAsyncVisit: z
    .object({
      cancelReasonCode: z.string().default(""),
      cancelReasonText: z.string().default(""),
    })
    .default({ cancelReasonCode: "", cancelReasonText: "" }),
  dxBillingCodes: z
    .array(dxBillingCodingSchema)
    .max(12, "At most 12 ICD-10 codes are allowed")
    .refine(
      (codes) => new Set(codes.map(({ code }) => code)).size === codes.length,
      { message: "Duplicate ICD-10 codes are not allowed" },
    )
    .default([]),
  selectedDiagnosisIds: z.array(z.string()).optional(),
  icd10: strictLegacyIcd10Schema,
  encounterTimeData: z.object({
    chartingTimeSpent: z
      .number()
      .min(1, "Total billable time must be at least 1 minute"),
    othersChartingTimeSpent: z
      .number()
      .min(0, "Others charting time spent must be >= 0"),
    avCallTimeSpent: z.number().min(0, "A/V call time must be 0 or more"),
    postEncounterActivities: z.array(
      z.object({
        activityType: z.enum([
          "work-note",
          "review-additional-records",
          "referral",
          "imaging-order",
          "ma-coordination",
        ]),
        displayName: z.string(),
        time: z.number().min(0, "Time spent must be >= 0"),
      }),
    ),
    patientType: z
      .enum(["new", "established"])
      .nullable()
      .refine((val): boolean => !!val, {
        message: "Patient type is required",
      }),
    billType: z.enum(["duration", "mdm"]),
    visitType: z.enum(["av"]),
  }),
  cptCode: z
    .object({
      coding: z.array(
        z.object({
          code: z.string(),
          system: z.string(),
          display: z.string(),
          version: z.string(),
        }),
      ),
      text: z.string(),
    })
    .refine((data) => data?.coding?.[0]?.code?.length, {
      message: "At least one CPT code is required",
    }),
  programEnrollment: z
    .object({
      enrollmentApproved: z.boolean().nullable(),
      encounterProgram: z.custom<EncounterProgram>().nullable().default(null),
    })
    .default({ enrollmentApproved: null, encounterProgram: null }),
});

export type VisitClosureFormData = z.output<
  typeof visitClosureFormSchemaWithDefaults
>;
export type VisitClosureSchemaInput = z.input<
  typeof visitClosureFormSchemaWithDefaults
>;
export type VisitClosureFormInput = VisitClosureFormData;

export const visitClosureFormSchema = visitClosureFormSchemaWithDefaults;

export const getVisitClosureFormSchema = ({
  requiresDxBillingCodes,
  requiresLegacyIcd10,
  requiresEnrollmentDecision,
}: {
  requiresDxBillingCodes: boolean;
  requiresLegacyIcd10: boolean;
  requiresEnrollmentDecision: boolean;
}) =>
  visitClosureFormSchemaWithDefaults
    .extend({
      icd10: requiresLegacyIcd10
        ? strictLegacyIcd10Schema
        : relaxedLegacyIcd10Schema,
    })
    .superRefine((data, context) => {
      if (requiresDxBillingCodes && data.dxBillingCodes.length < 1) {
        context.addIssue({
          code: "custom",
          message: "At least one billing code is required",
          path: ["dxBillingCodes"],
        });
      }
      if (
        requiresEnrollmentDecision &&
        data.programEnrollment.enrollmentApproved === null
      ) {
        context.addIssue({
          code: "custom",
          message: "An enrollment decision is required to close this visit",
          path: ["programEnrollment", "enrollmentApproved"],
        });
      }
    });
